import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import EnquireModal from '../components/EnquireModal';
import { productsAPI, categoriesAPI, getImageUrl } from '../utils/api';

const AllProducts = () => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [enquireModalOpen, setEnquireModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);

    // Pagination state
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const LIMIT = 12; // Adjusted limit for pagination

    // Debounced search term for API calls
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        // Only trigger debounce if search term actually changed from the current debounced value
        if (searchTerm === debouncedSearch && products.length > 0) return;

        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchProducts = async (currentPage, reset = false) => {
        try {
            const params = {
                page: currentPage,
                limit: LIMIT,
                status: 'published',
                _t: Date.now()
            };

            if (selectedCategory !== 'all') {
                params.category = selectedCategory;
            }

            if (debouncedSearch) {
                params.search = debouncedSearch;
            }

            const res = await productsAPI.getAll(params);
            const { products: newProducts, totalCount, totalPages } = res.data;

            setProducts(newProducts || []);
            setTotalCount(totalCount || 0);
            setTotalPages(totalPages || 1);
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    useEffect(() => {
        fetchProducts(1, true);
    }, [selectedCategory, debouncedSearch]);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const res = await categoriesAPI.getAll({ status: 'active' });
                const data = Array.isArray(res.data) ? res.data : (res.data?.categories || res.data?.data || []);
                setCategories(data);
            } catch (err) {
                console.error("Error fetching categories", err);
            }
        };
        fetchCategories();
    }, []);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
            setLoading(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            fetchProducts(newPage, true);
        }
    };

    const handleCategoryChange = (e) => {
        setSelectedCategory(e.target.value);
        setPage(1);
        setProducts([]);
    };

    const getCategoryName = (categoryId) => {
        if (!categoryId) return 'Uncategorized';
        const category = categories.find(cat => cat._id === categoryId || cat._id === categoryId?._id);
        if (!category) return 'Uncategorized';
        return category.name;
    };

    return (
        <>
            <section className="relative bg-gradient-to-br from-db-dark to-blue-900 text-white py-20 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <img
                        src="https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=1600"
                        alt="Products Background"
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-db-dark/80 to-transparent"></div>
                <div className="relative mx-auto px-4 sm:px-6 lg:px-8 text-center z-10" style={{ maxWidth: '90%' }}>
                    <nav className="text-sm text-gray-300 mb-6" data-aos="fade-up">
                        <Link to="/" className="hover:text-white transition">Home</Link> / <span className="text-white">All Products</span>
                    </nav>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" data-aos="fade-up" data-aos-delay="200">All Products</h1>
                    <p className="text-xl text-gray-300 max-w-3xl mx-auto" data-aos="fade-up" data-aos-delay="400">
                        Browse our complete catalog of barcode solutions and industrial automation products
                    </p>
                </div>
            </section>

            <section className="py-12 bg-white">
                <div className="mx-auto px-4 sm:px-6 lg:px-8 relative z-10" style={{ maxWidth: '90%' }}>
                    <div className="mb-8" data-aos="fade-up">
                        <div className="flex flex-col md:flex-row gap-4 mb-6">
                            <div className="flex-1">
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder="Search products..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                    <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                                </div>
                            </div>

                            <div className="md:w-64">
                                <select
                                    value={selectedCategory}
                                    onChange={handleCategoryChange}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="all">All Categories</option>
                                    {categories.filter(c => !c.parentCategory).map(category => (
                                        <option key={category._id} value={category._id}>
                                            {category.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="text-sm font-medium text-gray-600 flex items-center justify-center md:justify-start gap-2">
                            <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full">
                                {totalCount} Products Found
                            </span>
                            {totalCount > 0 && (
                                <span className="text-gray-400">
                                    Showing {((page - 1) * LIMIT) + 1} - {Math.min(page * LIMIT, totalCount)}
                                </span>
                            )}
                            {selectedCategory !== 'all' && <span className="text-gray-400">| Category: {getCategoryName(selectedCategory)}</span>}
                        </div>
                    </div>

                    {loading && products.length === 0 ? (
                        <div className="text-center py-12">
                            <i className="fas fa-spinner fa-spin text-4xl text-gray-400 mb-4"></i>
                            <p className="text-gray-600">Loading products...</p>
                        </div>
                    ) : products.length === 0 ? (
                        <div className="text-center py-12">
                            <i className="fas fa-box-open text-4xl text-gray-400 mb-4"></i>
                            <p className="text-gray-600 text-lg mb-2">No products found</p>
                            <p className="text-gray-500 text-sm">
                                {searchTerm || selectedCategory !== 'all'
                                    ? 'Try adjusting your search or filter criteria'
                                    : 'No products available at the moment'}
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                {products.map((product, index) => (
                                    <div
                                        key={`${product._id}-${index}`}
                                        className="bg-white border-2 border-transparent hover:border-blue-500 rounded-lg shadow-md overflow-hidden relative product-card transition-all duration-300"
                                        data-aos="fade-up"
                                        data-aos-delay={index * 50}
                                    >
                                        <Link to={`/product/${product._id}`} className="block group/details">
                                            <div className="relative h-48 overflow-hidden bg-gray-100">
                                                {product.images && product.images[0] ? (
                                                    <img
                                                        src={getImageUrl(product.images[0])}
                                                        alt={product.name}
                                                        className="w-full h-full object-cover transition-transform duration-500"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300">
                                                        <i className="fas fa-image text-4xl text-gray-400"></i>
                                                    </div>
                                                )}
                                                {product.featured && (
                                                    <div className="absolute top-2 right-2 bg-db-red text-white px-2 py-1 rounded text-xs font-semibold z-10">
                                                        Featured
                                                    </div>
                                                )}
                                            </div>
                                            <div className="p-4 pb-0">
                                                <div className="mb-2 flex items-center justify-between">
                                                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                                        {getCategoryName(product.category)}
                                                    </span>
                                                    {product.brand && (
                                                        <span className="text-xs font-semibold text-blue-600">
                                                            {product.brand}
                                                        </span>
                                                    )}
                                                </div>
                                                <h3 className="font-semibold text-lg text-gray-900 mb-2 transition-colors line-clamp-2">
                                                    {product.name}
                                                </h3>
                                                {product.shortDescription && (
                                                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                                                        {product.shortDescription}
                                                    </p>
                                                )}
                                            </div>
                                        </Link>

                                        <div className="p-4 pt-0">
                                            <div className="flex gap-2">
                                                <Link
                                                    to={`/product/${product._id}`}
                                                    className="flex-1 text-center bg-db-blue hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition font-semibold text-sm"
                                                >
                                                    Details
                                                </Link>
                                                <button
                                                    onClick={() => {
                                                        setSelectedProduct(product);
                                                        setEnquireModalOpen(true);
                                                    }}
                                                    className="flex-1 bg-gradient-to-r from-db-red to-red-700 hover:from-red-700 hover:to-red-800 text-white px-4 py-2 rounded-lg transition font-semibold text-sm"
                                                >
                                                    Enquire
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Enhanced Pagination UI */}
                            {totalPages > 1 && (
                                <div className="mt-12 flex flex-wrap justify-center items-center gap-2" data-aos="fade-up">
                                    <button
                                        onClick={() => handlePageChange(page - 1)}
                                        disabled={page === 1}
                                        className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-600 disabled:hover:border-gray-200"
                                        title="Previous Page"
                                    >
                                        <i className="fas fa-chevron-left text-sm"></i>
                                    </button>

                                    {[...Array(totalPages)].map((_, idx) => {
                                        const pageNum = idx + 1;
                                        // Simple logic to show current, previous, next and first/last
                                        if (
                                            pageNum === 1 ||
                                            pageNum === totalPages ||
                                            (pageNum >= page - 1 && pageNum <= page + 1)
                                        ) {
                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => handlePageChange(pageNum)}
                                                    className={`w-10 h-10 flex items-center justify-center rounded-lg border transition-all font-bold ${page === pageNum
                                                        ? 'bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-200'
                                                        : 'border-gray-200 text-gray-600 hover:border-blue-600 hover:text-blue-600 bg-white'
                                                        }`}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        } else if (
                                            pageNum === page - 2 ||
                                            pageNum === page + 2
                                        ) {
                                            return <span key={pageNum} className="text-gray-400 px-1">...</span>;
                                        }
                                        return null;
                                    })}

                                    <button
                                        onClick={() => handlePageChange(page + 1)}
                                        disabled={page === totalPages}
                                        className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-600 disabled:hover:border-gray-200"
                                        title="Next Page"
                                    >
                                        <i className="fas fa-chevron-right text-sm"></i>
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </section>

            <EnquireModal
                isOpen={enquireModalOpen}
                onClose={() => {
                    setEnquireModalOpen(false);
                    setSelectedProduct(null);
                }}
                product={selectedProduct}
            />
        </>
    );
};

export default AllProducts;
